import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { FiCopy, FiCheck, FiMessageSquare, FiMail, FiExternalLink } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';

const cfg = () => {
  const token = localStorage.getItem('authToken');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// "My Digital Card" panel for the Profile page: QR, copy link, text it, email it.
// Toggles between the user's own card and the company card.
export default function MyDigitalCard() {
  const [links, setLinks] = useState(null);
  const [which, setWhich] = useState('my');      // 'my' | 'company'
  const [copied, setCopied] = useState(false);
  const [to, setTo] = useState('');
  const [via, setVia] = useState(null);          // 'sms' | 'email' | null
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [showCell, setShowCell] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/my-card`, cfg());
        setLinks(res.data);
        setShowCell(!!res.data.show_cell);
      } catch (e) { /* panel just stays hidden */ }
    })();
  }, []);

  const toggleCell = async (next) => {
    setShowCell(next);
    try {
      await axios.put(`${API_URL}/my-card/show-cell`, { show_cell: next }, cfg());
    } catch (e) {
      setShowCell(!next);   // revert on failure
    }
  };

  if (!links) return null;
  const active = which === 'my' ? links.my : links.company;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(active.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      window.prompt('Copy this link:', active.url);
    }
  };

  const send = async () => {
    if (!to.trim()) return;
    setSending(true);
    setNote('');
    try {
      const res = await axios.post(`${API_URL}/cards/${active.slug}/share`, { via, to: to.trim() }, cfg());
      setNote(res.data?.message || 'Sent.');
      setTo('');
      setVia(null);
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not send.');
    } finally {
      setSending(false);
    }
  };

  const tab = (key, label) => (
    <button
      onClick={() => { setWhich(key); setVia(null); setNote(''); }}
      style={{
        padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        border: '1px solid ' + (which === key ? '#14532d' : '#e2e8f0'),
        background: which === key ? '#14532d' : 'white',
        color: which === key ? 'white' : '#475569',
      }}
    >{label}</button>
  );

  const btn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: '1px solid #e2e8f0', background: 'white', color: '#334155', cursor: 'pointer', flex: 1,
  };

  return (
    <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 20, marginBottom: 15, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#666' }}>My Digital Card</h3>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {tab('my', 'My Card')}
        {tab('company', 'Company Card')}
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ background: 'white', padding: 8, border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <QRCodeCanvas value={active.url} size={112} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
            Scan to save {which === 'my' ? 'me' : 'us'} — or share the link:
          </div>
          <div style={{ fontSize: 12, color: '#334155', wordBreak: 'break-all', marginBottom: 10 }}>{active.url}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btn} onClick={copy}>
              {copied ? <><FiCheck size={13} /> Copied</> : <><FiCopy size={13} /> Copy link</>}
            </button>
            <a style={{ ...btn, textDecoration: 'none' }} href={active.url} target="_blank" rel="noopener noreferrer">
              <FiExternalLink size={13} /> Preview
            </a>
          </div>
        </div>
      </div>

      {which === 'my' && links.cell && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
          <input type="checkbox" checked={showCell} onChange={(e) => toggleCell(e.target.checked)} style={{ width: 16, height: 16 }} />
          Show my cell ({links.cell}) on my card
        </label>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button style={{ ...btn, background: via === 'sms' ? '#eff6ff' : 'white' }} onClick={() => { setVia('sms'); setTo(''); setNote(''); }}>
          <FiMessageSquare size={13} /> Text it
        </button>
        <button style={{ ...btn, background: via === 'email' ? '#eff6ff' : 'white' }} onClick={() => { setVia('email'); setTo(''); setNote(''); }}>
          <FiMail size={13} /> Email it
        </button>
      </div>

      {via && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={via === 'sms' ? 'Phone number' : 'Email address'}
            style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
          />
          <button
            onClick={send}
            disabled={sending || !to.trim()}
            style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#14532d', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: (sending || !to.trim()) ? 0.6 : 1 }}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}

      {note && <div style={{ marginTop: 8, fontSize: 13, color: '#475569' }}>{note}</div>}
    </div>
  );
}
